'use client'

import Image from 'next/image'
import Link from 'next/link'
import { accountUrl } from '@/lib/config'
import { businessOccasions } from '@/lib/business-occasions'
import { BusinessVibeViteInActionSection } from '@/components/MarketingHomeCelebrations'
import { TrackedCTA } from '@/components/TrackedCTA'
import { useCtaTracking } from '@/hooks/useCtaTracking'
import type { PageContext } from '@/lib/cta-tracking'

const businessPageContext: PageContext = {
  page_type: 'business_hub',
  page_name: 'Business Overview',
  page_slug: 'business',
  page_category: 'business',
}

const Arrow = () => <span aria-hidden="true">→</span>

const trustBar = [
  ['No app required', 'Contributors join from any device'],
  ['Private by default', 'Invite-only walls, never public links'],
  ['Built for HR & managers', 'Ready-made templates for every milestone'],
]


const howItWorks = [
  ['01', 'Create a Wall', 'Choose a template and personalize it in minutes.'],
  ['02', 'Invite Contributors', 'Share a secure link with your team — no downloads required.'],
  ['03', 'Collect Messages', 'Employees add photos, videos, GIFs, and comments.'],
  ['04', 'Celebrate Together', 'Present the finished wall live or keep it as a lasting keepsake.'],
]

const featureCategories = [
  { category: 'Collaboration', items: 'Team & guest contributors · Multiple organization admins · Secure, invite-only sharing' },
  { category: 'Rich Media', items: 'Written messages · Photos · Videos · Audio · GIFs' },
  { category: 'Administration', items: 'Wall moderation controls · Advanced admin controls · Privacy controls' },
  { category: 'Presentation', items: 'Live slideshow · Keepsake wall that stays accessible' },
]

const screenshots = [
  {
    label: 'Organization Dashboard',
    text: 'Manage every live and delivered wall for your company from one dashboard.',
    image: '/images/home/product-screenshots/business-wall-hub-20260903.png',
    imageAlt: 'VibeVite Business Walls dashboard showing live and delivered employee recognition walls',
    imageWidth: 1345,
    imageHeight: 525,
    bgClass: 'bg-[#E3F7DB]',
  },
  {
    label: 'Wall Templates',
    text: 'Start from a ready-made template for appreciation, milestones, or farewells.',
    image: '/images/home/product-screenshots/business-wall-templates-20260903.png',
    imageAlt: 'VibeVite Business Walls template gallery for employee celebrations and achievements',
    imageWidth: 1366,
    imageHeight: 416,
    bgClass: 'bg-[#E1EEFF]',
  },
]

const whyChoose = [
  ['Build a Recognition Culture', 'Make celebrating milestones part of how your company operates, not an afterthought.'],
  ['Increase Participation', 'Anyone can contribute in minutes, from any device.'],
  ['Save HR Time', 'Ready-made templates simplify every celebration, from onboarding to retirement.'],
  ['Strengthen Employee Engagement', 'Every employee has a voice in celebrating their peers.'],
  ['Preserve Memories', 'Walls stay accessible long after the event, so appreciation lasts.'],
]

const comparisonRows = [
  ['One message', 'Unlimited messages'],
  ['Paper only', 'Photos, videos, GIFs, audio'],
  ['No collaboration', 'Everyone contributes'],
  ['Static', 'Interactive'],
  ['Easy to lose', 'Digital keepsake, always accessible'],
]

const pricingPreview = [
  { name: 'One-Time Wall', note: 'Pay once, for one celebration', text: 'A simple option for a farewell, birthday, milestone, or team celebration.' },
  { name: 'Starter', note: 'From $35/month · up to 50 employees', text: 'Make recognition easy for a small company or growing team.' },
  { name: 'Professional', note: 'Team pricing · based on employee count', text: 'Advanced execution for high-volume contributor engagement.', badge: 'Recommended' },
  { name: 'Enterprise', note: 'Let’s talk', text: 'A tailored recognition program for complex organization needs.' },
]

const security = [
  'Private, invite-only walls — nothing is posted publicly',
  'Wall moderation controls so admins review contributions before they go live',
  'Role-based admin controls, with multiple organization admins on Professional plans',
  'SSO and advanced access controls available for Enterprise organizations',
]

const faqs = [
  {
    question: 'Do employees need an account to contribute?',
    answer: 'No — hosts share a wall link, and contributors can add messages, photos, and videos without creating an account.',
  },
  {
    question: 'Can external guests contribute to a wall?',
    answer: 'Yes. Clients, vendors, and former colleagues can contribute if you share the wall link with them, even though the wall itself stays private and invite-only.',
  },
  {
    question: 'Can we moderate posts before they’re published?',
    answer: 'Yes — wall moderation controls let admins review contributions before they go live.',
  },
  {
    question: 'Does the wall stay accessible after the celebration?',
    answer: 'Yes — the wall remains as a keepsake your team can revisit anytime. It isn’t taken down after the event.',
  },
  {
    question: 'How many employees can use VibeWall on our plan?',
    answer: 'It depends on your plan. Starter supports teams up to 50 employees, while Professional and Enterprise scale for larger organizations based on employee count.',
  },
  {
    question: 'Is VibeWall mobile-friendly?',
    answer: 'Yes — contributors can add messages, photos, and videos right from their phone browser, no app required.',
  },
]

export function BusinessLandingSections() {
  useCtaTracking(businessPageContext)

  return (
    <main className="bg-white text-[#1B0044]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white text-[#2B0A4A]">
        <div
          className="pointer-events-none absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('/images/home/hero-icons-bg.svg')" }}
        />

        <div className="relative mx-auto max-w-7xl px-5 py-16 text-center md:px-12 md:py-24 lg:py-32">
          <span className="mb-4 inline-block rounded-full bg-[#2B0A4A]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#7A3C83]">
            VibeWall for Business
          </span>
          <h1 className="mb-4 text-2xl font-bold leading-tight sm:text-3xl md:text-4xl lg:mb-6 lg:text-5xl xl:text-6xl">
            Celebrate the People Behind Your Success
          </h1>
          <p className="mx-auto mb-8 max-w-3xl text-sm leading-relaxed text-[#2B0A4A]/70 sm:text-base md:text-lg lg:text-xl">
            The employee recognition platform that turns birthdays, work anniversaries, and team milestones into
            collaborative digital celebration walls — built for HR teams and managers who want appreciation to stick.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <TrackedCTA
              href={`${accountUrl}/app/business/signup`}
              pageContext={businessPageContext}
              ctaContext={{
                cta_name: 'start_wall',
                cta_label: 'Start Your Wall',
                cta_position: 'hero',
                cta_variant: 'primary',
              }}
              className="rounded-xl bg-[#2B0A4A] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#1B0044]"
            >
              Start Your Wall
            </TrackedCTA>
            <TrackedCTA
              href="/pricing#business-pricing"
              pageContext={businessPageContext}
              ctaContext={{
                cta_name: 'view_pricing',
                cta_label: 'View Pricing',
                cta_position: 'hero',
                cta_variant: 'secondary',
              }}
              className="rounded-xl border-2 border-[#2B0A4A]/20 px-8 py-3.5 font-semibold text-[#2B0A4A] transition-colors hover:border-[#2B0A4A] hover:bg-[#FFF8EC]"
            >
              View Pricing
            </TrackedCTA>
          </div>
          <p className="mt-5 text-xs text-[#2B0A4A]/50 md:text-sm lg:mt-6">
            Private by default, no app required, and ready for every team milestone.
          </p>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#FFF8EC] py-10 md:py-12">
        <div className="mx-auto grid max-w-6xl gap-4 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustBar.map(([title, subtitle]) => (
            <div key={title} className="rounded-2xl border border-[#2B0A4A]/10 bg-white px-5 py-4 shadow-sm">
              <p className="text-base font-bold text-[#2B0A4A]">{title}</p>
              <p className="mt-1 text-xs leading-relaxed text-[#1B0044]/65">{subtitle}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What is VibeWall */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">What is VibeWall?</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            One Wall. Every Message. One Unforgettable Celebration.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#1B0044]/75 md:text-lg">
            VibeWall is a collaborative employee celebration platform that makes it easy for teams to say thank you,
            together. Coworkers contribute messages, photos, videos, GIFs, and reactions to a shared digital wall — for
            birthdays, work anniversaries, promotions, retirements, farewells, and company milestones. No more
            single-signature cards. Just one place where every voice on the team can show up for a colleague.
          </p>
        </div>
      </section>

      {/* Built for every workplace celebration */}
      <section className="bg-[#FFF8EC] py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Use cases</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            Built for Every Workplace Celebration
          </h2>
        </div>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {businessOccasions.map((item) => (
            <TrackedCTA
              key={item.title}
              href={item.href}
              pageContext={businessPageContext}
              ctaContext={{
                cta_name: 'explore_occasion',
                cta_label: item.title,
                cta_position: 'occasions_grid',
              }}
              className="rounded-2xl border border-[#2B0A4A]/10 bg-white p-5 shadow-sm transition-colors hover:border-[#FFC933]"
            >
              <span className="text-2xl">{item.icon}</span>
              <h3 className="mt-3 text-base font-bold text-[#2B0A4A]">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#1B0044]/70">{item.text}</p>
            </TrackedCTA>
          ))}
        </div>
      </section>

      {/* How VibeWall works */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">How VibeWall works</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            From Idea to Celebration in Four Simple Steps
          </h2>
        </div>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 px-6 md:grid-cols-2 xl:grid-cols-4">
          {howItWorks.map(([step, title, desc]) => (
            <article key={step} className="rounded-2xl border-t-2 border-[#FFC933] bg-[#FFF8EC] p-6">
              <span className="text-xs font-extrabold text-[#FF8A3D]">{step}</span>
              <h3 className="mt-3 text-lg font-semibold text-[#2B0A4A]">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#1B0044]/70">{desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Everything you need */}
      <section className="bg-[#FFF8EC] py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Features</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            Everything Your Team Needs to Celebrate Well
          </h2>
        </div>
        <div className="mx-auto mt-10 grid max-w-5xl gap-4 px-6 sm:grid-cols-2">
          {featureCategories.map(({ category, items }) => (
            <div key={category} className="rounded-2xl border border-[#2B0A4A]/10 bg-white p-6 shadow-sm">
              <h3 className="text-base font-bold text-[#2B0A4A]">{category}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#1B0044]/70">{items}</p>
            </div>
          ))}
        </div>
      </section>

      <BusinessVibeViteInActionSection />

      {/* Why teams choose VibeWall */}
      <section className="bg-[#FFF8EC] py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Why teams choose it</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            Why HR Teams and Managers Choose VibeWall
          </h2>
        </div>
        <div className="mx-auto mt-8 max-w-3xl px-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-7">
            <ul className="space-y-4">
              {whyChoose.map(([title, text]) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFC933]" />
                  <span className="text-sm leading-relaxed text-[#1B0044]/85 sm:text-base">
                    <b className="text-[#2B0A4A]">{title}</b> — {text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Comparison</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            VibeWall vs. the Traditional Office Card
          </h2>
        </div>
        <div className="mx-auto mt-10 max-w-3xl overflow-x-auto rounded-3xl border border-[#E9DDCE] px-6 shadow-[0_18px_55px_rgba(43,10,74,0.07)] md:px-0">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[#E9DDCE] bg-[#FFF8EC]">
                <th className="px-5 py-4 font-semibold text-[#1B0044]">Traditional Card</th>
                <th className="bg-[#FFF2B8] px-5 py-4 font-semibold text-[#1B0044]">VibeWall</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row[0]} className="border-b border-[#F1E8DF] last:border-b-0">
                  <td className="px-5 py-4 text-[#6B6170]">{row[0]}</td>
                  <td className="bg-[#FFF9D6]/40 px-5 py-4 font-medium text-[#1B0044]">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="bg-[#FFF8EC] py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Pricing</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">Plans for Every Team Size</h2>
        </div>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {pricingPreview.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border p-6 shadow-sm ${plan.badge ? 'border-2 border-[#2B0A4A] bg-white' : 'border-[#2B0A4A]/10 bg-white'}`}
            >
              {plan.badge && (
                <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FFC933] px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#1B0044]">
                  {plan.badge}
                </span>
              )}
              <h3 className="mt-2 text-lg font-bold text-[#2B0A4A]">{plan.name}</h3>
              <p className="mt-1 text-xs font-semibold text-[#FF8A3D]">{plan.note}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#1B0044]/70">{plan.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <TrackedCTA
            href="/pricing#business-pricing"
            pageContext={businessPageContext}
            ctaContext={{
              cta_name: 'view_pricing',
              cta_label: 'View Pricing',
              cta_position: 'pricing_preview',
              cta_variant: 'secondary',
            }}
            className="inline-flex rounded-xl bg-[#2B0A4A] px-7 py-3 font-semibold text-white transition-colors hover:bg-[#1B0044]"
          >
            View Pricing <Arrow />
          </TrackedCTA>
        </div>
      </section>

      {/* Security & privacy */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Security & privacy</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">
            Privacy and Access Controls You Can Trust
          </h2>
        </div>
        <div className="mx-auto mt-8 max-w-3xl px-6">
          <div className="rounded-2xl border border-gray-200 bg-[#FFF8EC] p-7">
            <ul className="space-y-3">
              {security.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[#1B0044]/85 sm:text-base">
                  <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFC933]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FFF8EC] px-6 py-16 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">FAQ</p>
          <h2 className="mt-3 text-2xl font-bold text-[#2B0A4A] md:text-4xl">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-3xl">
          {faqs.map(({ question, answer }) => (
            <details key={question} className="border-t border-[#D8CBBB] py-5 last:border-b">
              <summary className="cursor-pointer list-none text-base font-semibold text-[#1B0044] marker:content-none">
                <span className="flex items-center justify-between gap-4">
                  {question}
                  <span className="text-2xl text-[#FF8A3D]" aria-hidden="true">+</span>
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-[#1B0044]/70">{answer}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FF8A3D] via-[#FFC933] to-[#FFE17B] px-5 py-14 text-center md:px-8 md:py-20">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#2B0A4A]">Ready when you are</p>
        <h2 className="mx-auto mt-4 max-w-5xl text-balance text-4xl font-bold leading-tight tracking-[-0.045em] text-[#1B0044] sm:text-5xl md:text-6xl">
          Create More Meaningful Workplace Celebrations
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[#1B0044]/80 md:text-lg">
          Whether you&apos;re celebrating one employee or your entire organization, VibeWall helps teams connect
          through shared memories, appreciation, and recognition — no matter where they work.
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
          <TrackedCTA
            href={`${accountUrl}/app/business/signup`}
            pageContext={businessPageContext}
            ctaContext={{
              cta_name: 'start_wall',
              cta_label: 'Start Your Wall',
              cta_position: 'bottom',
              cta_variant: 'primary',
            }}
            className="inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#2B0A4A] to-[#1B0044] px-6 text-sm font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl"
          >
            Start Your Wall
          </TrackedCTA>
          <TrackedCTA
            href="/pricing#business-pricing"
            pageContext={businessPageContext}
            ctaContext={{
              cta_name: 'view_pricing',
              cta_label: 'View Pricing',
              cta_position: 'bottom',
              cta_variant: 'secondary',
            }}
            className="inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border-2 border-[#2B0A4A] px-6 text-sm font-semibold text-[#2B0A4A] transition hover:bg-[#2B0A4A] hover:text-white"
          >
            View Pricing →
          </TrackedCTA>
        </div>
      </section>
    </main>
  )
}
